import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ErrorNote, Field, useSubmit } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { PhoneField } from "../components/phone-field";

/**
 * Public account deletion request (Google Play requires a web link that works without the app). The answer
 * is the same whether or not the account exists; the association's administration reviews every request.
 */
export function AccountDeletionPage() {
  const { busy, error, run } = useSubmit();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ phone: "", note: "", website: "" });

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(
      async () => {
        await api("/v1/public/deletion-requests", {
          method: "POST",
          json: {
            phone: form.phone.trim(),
            note: form.note.trim() || undefined,
            website: form.website || undefined,
          },
        });
        setSent(true);
      },
      (err) =>
        err instanceof ApiError && err.status === 429
          ? "أُرسلت طلبات كثيرة من هذا الجهاز. حاول لاحقًا."
          : errorText(err),
    );
  }

  return (
    <main className="public">
      <div className="public-card">
        <div className="logo">
          <img src="/logo-dark.png" alt="مُبين" />
        </div>
        {sent ? (
          <section className="card section" aria-live="polite">
            <h1 style={{ fontSize: 22 }}>وصل طلبك</h1>
            <p>
              إن كان الحساب موجودًا فستراجع إدارة الجمعية الطلب وتتواصل مع صاحبه خلال المدة المذكورة في سياسة
              الخصوصية. لا حاجة لإعادة الإرسال.
            </p>
          </section>
        ) : (
          <form className="card section form" onSubmit={submit} noValidate>
            <div className="page-title">
              <h1>طلب حذف الحساب</h1>
              <p>
                يُعطَّل الحساب بعد مراجعة الإدارة، ويُعامل السجل التعليمي وفق{" "}
                <Link to="/privacy">سياسة الخصوصية</Link>. يمكنك أيضًا الطلب من داخل التطبيق: الحساب ← طلب حذف
                الحساب.
              </p>
            </div>
            <ErrorNote error={error} />
            <Field id="d-id" label="رقم الجوال المسجل">
              <PhoneField
                id="d-id"
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                required
              />
            </Field>
            <Field id="d-note" label="ملاحظة (اختياري)" hint="مثل: سبب الطلب، أو وسيلة تواصل">
              <textarea
                id="d-note"
                maxLength={500}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </Field>
            <div className="honeypot" aria-hidden="true">
              <label htmlFor="d-website">الموقع</label>
              <input
                id="d-website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" disabled={busy || form.phone.trim().length < 3}>
                {busy ? "جارٍ الإرسال…" : "إرسال الطلب"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
